// components/workspacePickOverlay.ts
// What the workspace pick overlays share (WorkspaceColumnPickOverlay, RecastZonePickOverlay): one pick on the canvas,
// its project's viewport, its toolbar beside the frame, its price and its Escape.
import type React from 'react';
import { useEffect } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { isInertSurface } from '#lib/keyboard-shortcuts';
import { estimateStudioModelCredits } from '#lib/model-registry';
import { getDefaultParamsForModel } from '#lib/models';
import type { Rect } from '#types/types';

const TOOLBAR_MARGIN_PX = 8;
const TOOLBAR_WIDTH_PX = 176;
const TOOLBAR_HEIGHT_PX = 72;
export const PICK_INTERACTION_STYLE: React.CSSProperties = { position: 'absolute', inset: 0, zIndex: 70, cursor: 'crosshair', userSelect: 'none' };
export const PICK_FLOATING_UI_STYLE: React.CSSProperties = { position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 80 };

const finiteOr = (value: unknown, fallback: number) => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);

/** The committed zoom and pan of the pick's project. */
export const useProjectViewport = (projectId: string | null) => {
  const zoom = useKernelStore((state) => finiteOr(projectId ? state.inventory.Project.registry[projectId]?.runtime?.zoomLevel : undefined, 1));
  const panX = useKernelStore((state) => finiteOr(projectId ? state.inventory.Project.registry[projectId]?.runtime?.panOffset?.x : undefined, 0));
  const panY = useKernelStore((state) => finiteOr(projectId ? state.inventory.Project.registry[projectId]?.runtime?.panOffset?.y : undefined, 0));
  return { zoom, panX, panY };
};

/** Escape first leaves a field of the pick being typed in, so the pick and its notes survive it, then cancels the pick. */
export const usePickEscape = (pickAttribute: string, cancel: 'cancelWorkspaceColumnPick' | 'cancelRecastZonePick') => {
  const { kernel } = useKernel();
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || isInertSurface(kernel.current?.canvasContainerRef.current)) return;
      // Before the canvas's own Escape, which would also drop the active tool.
      event.preventDefault();
      event.stopPropagation();
      const focused = document.activeElement;
      if (focused instanceof HTMLInputElement && focused.closest(`[${pickAttribute}]`)) focused.blur();
      else kernel.current?.[cancel]();
    };
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  }, [kernel, pickAttribute, cancel]);
};

/** The toolbar beside the frame's right edge, else its left one, kept inside the canvas. */
export const pickToolbarStyle = (frame: Rect, panX: number, panY: number, zoom: number, host: HTMLElement): React.CSSProperties => {
  const viewportWidth = host.clientWidth, viewportHeight = host.clientHeight;
  const besideRight = panX + (frame.x + frame.w) * zoom + TOOLBAR_MARGIN_PX;
  const toolbarLeft = besideRight + TOOLBAR_WIDTH_PX > viewportWidth - TOOLBAR_MARGIN_PX
    ? panX + frame.x * zoom - TOOLBAR_MARGIN_PX - TOOLBAR_WIDTH_PX
    : besideRight;
  return {
    position: 'absolute',
    width: TOOLBAR_WIDTH_PX,
    left: Math.max(TOOLBAR_MARGIN_PX, Math.min(toolbarLeft, viewportWidth - TOOLBAR_WIDTH_PX - TOOLBAR_MARGIN_PX)),
    top: Math.max(TOOLBAR_MARGIN_PX, Math.min(panY + frame.y * zoom + TOOLBAR_MARGIN_PX, viewportHeight - TOOLBAR_HEIGHT_PX - TOOLBAR_MARGIN_PX)),
  };
};

/** A run's price: one quote of its recipe's generation per step. */
export const quoteRecipeCredits = (model: string, params: Record<string, unknown>, prompt: string, steps: number): number | null => {
  const quote = estimateStudioModelCredits(model, { ...getDefaultParamsForModel(model)?.(), ...params, prompt });
  return typeof quote === 'number' && Number.isFinite(quote) ? Math.round(quote * steps * 100) / 100 : null;
};
